import { Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, EMPTY, Observable, Subject } from 'rxjs';
import { catchError } from 'rxjs/operators';

import {
    SESSION_END_POLICY,
    SESSION_NOTICE_TIMEOUT_MS,
    SESSION_SCOPED_STORAGE_KEYS,
    SESSION_WELCOME_ROUTE
} from '../constants/session.constant';
import { SessionAccountScope, SessionEndReason } from '../enums/session-end-reason.enum';
import { StorageKey } from '../enums/storage-key.enum';
import {
    SESSION_LOGOUT_TRANSPORT,
    SESSION_MODAL_CLOSER,
    SessionEndPolicy,
    SessionLogoutTransport,
    StoredSessionAccount
} from '../interfaces/session.interface';
import {
    getLocalStorageValue,
    removeLocalStorageValue,
    setLocalStorageValue
} from '../utils/local-storage.utils';

/**
 * The single source of truth for ending a session.
 *
 * Nothing else in the application clears auth storage, calls the logout
 * endpoint, or redirects to the welcome screen. Callers state *why* the session
 * is ending via `SessionEndReason`; `SESSION_END_POLICY` decides what that
 * means. Adding a new way to log out is a new enum member plus a policy row.
 */
@Injectable({ providedIn: 'root' })
export class SessionService {
    private readonly router = inject(Router);
    private readonly toastr = inject(ToastrService);
    private readonly injector = inject(Injector);

    private readonly isEndingSessionSubject$ = new BehaviorSubject<boolean>(false);
    private readonly isManagedNavigationSubject$ = new BehaviorSubject<boolean>(false);
    private readonly sessionEndedSubject$ = new Subject<SessionEndReason>();

    /** True once this teardown's redirect has actually landed on the welcome screen. */
    private hasReachedWelcomeRoute = false;

    /** True while a redirect is awaiting the router, so a burst does not queue several. */
    private isWelcomeRedirectInFlight = false;

    /** True from the moment a teardown starts until the next successful sign-in. */
    readonly isEndingSession$: Observable<boolean> = this.isEndingSessionSubject$.asObservable();

    /**
   * Emits once per completed teardown. Consumers use this to drop their own
   * in-memory state without `SessionService` needing to know they exist.
   */
    readonly sessionEnded$: Observable<SessionEndReason> = this.sessionEndedSubject$.asObservable();

    /**
   * Synchronous read for `canDeactivate` guards, which must answer immediately
   * and cannot await an observable.
   */
    get isEndingSession(): boolean {
        return this.isEndingSessionSubject$.value;
    }

    /**
   * What `canDeactivate` guards should ask. True whenever the application is
   * deliberately moving the user away from their work — either because the
   * session is ending, or because the app is switching them to another entity.
   * In both cases prompting about unsaved changes would be noise.
   */
    get shouldBypassUnsavedChanges(): boolean {
        return this.isEndingSession || this.isManagedNavigationSubject$.value;
    }

    /**
   * Declares an app-driven navigation that is not a session end — switching or
   * removing the current entity. Cleared by the next sign-in, exactly like the
   * teardown latch.
   */
    beginManagedNavigation(): void {
        this.isManagedNavigationSubject$.next(true);
    }

    /**
   * Marks a new session as live. Called when credentials are stored after a
   * successful sign-in; without it the latches would stay closed and every
   * unsaved-changes guard would remain permanently suppressed.
   */
    startSession(): void {
        this.isEndingSessionSubject$.next(false);
        this.isManagedNavigationSubject$.next(false);
        this.hasReachedWelcomeRoute = false;
        this.isWelcomeRedirectInFlight = false;

        // The teardown is over and modals are legitimate again.
        this.setModalTeardownGuard(false);
    }

    /**
   * Ends the current session. Safe to call repeatedly — a dead session
   * typically fails several in-flight requests at once, and only the first call
   * does the once-only work (revoke, storage wipe, `sessionEnded$`, notice).
   *
   * Closing modals and reaching the welcome screen are *not* once-only. Both
   * run on every call, because `isEndingSession` is a one-way latch: it only
   * reopens on the next sign-in. If the first redirect never lands, the user is
   * left on a protected screen with dead credentials, and every later failed
   * request would otherwise be a silent no-op here — no redirect, and no toast
   * either, since `ApiErrorInterceptor` suppresses those during a teardown.
   */
    endSession(reason: SessionEndReason): void {
        const policy: SessionEndPolicy = SESSION_END_POLICY[reason];

        // Both deliberately ahead of the latch. Closing handles what is on screen
        // now; the guard handles what Bootstrap puts back afterwards, which is the
        // case a single sweep can never win — see `ModalService.setTeardownGuard`.
        this.setModalTeardownGuard(true);
        this.closeAllModals();

        if (this.isEndingSession) {
            // The once-only work already ran; only the redirect is worth retrying,
            // and silently — this teardown's notice has already been shown.
            this.navigateToWelcome(policy, false);
            return;
        }
        this.isEndingSessionSubject$.next(true);
        this.hasReachedWelcomeRoute = false;

        // Revoke before clearing: AuthInterceptor reads the token out of storage
        // when the request is dispatched, and dispatch happens on subscribe.
        if (policy.revokeOnServer) {
            this.revokeDeviceSession();
        }

        this.clearStoredSession(policy.accountScope);
        this.sessionEndedSubject$.next(reason);

        if (!policy.navigateToWelcome) {
            this.showNotice(policy);
            return;
        }

        this.navigateToWelcome(policy, true);
    }

    /**
   * Sends the user to the welcome screen, at most once per attempt in flight.
   *
   * `announceOnSettle` carries the notice, which must wait for the redirect to
   * settle: HelperService clears every toast on NavigationEnd, so a notice
   * raised before navigating is wiped the instant the welcome screen loads —
   * exactly the "toast flashes and vanishes" behaviour this replaces. Raised on
   * a failed navigation too; the user still needs to know their session ended.
   */
    private navigateToWelcome(policy: SessionEndPolicy, announceOnSettle: boolean): void {
        if (!policy.navigateToWelcome || this.isWelcomeRedirectInFlight || this.hasReachedWelcomeRoute) {
            return;
        }
        this.isWelcomeRedirectInFlight = true;

        // `navigate` resolves false when a guard or a competing navigation cancels
        // the redirect. That is not an error, but it does mean the user is still on
        // the screen they had no credentials for — so the next failed request must
        // be allowed to try again.
        this.router.navigate([SESSION_WELCOME_ROUTE])
            .then(didNavigate => this.settleWelcomeRedirect(didNavigate !== false, policy, announceOnSettle))
            .catch(() => this.settleWelcomeRedirect(false, policy, announceOnSettle));
    }

    private settleWelcomeRedirect(
        hasLanded: boolean,
        policy: SessionEndPolicy,
        announceOnSettle: boolean
    ): void {
        this.isWelcomeRedirectInFlight = false;
        this.hasReachedWelcomeRoute = hasLanded;

        // Swept a second time, now that the route component is gone. Opening a
        // Bootstrap dialog is animated, so one triggered just before the teardown
        // can finish opening *after* the first sweep — and finishing is exactly
        // when Bootstrap reparents it to <body>, out of reach of the navigation
        // that was supposed to take it down. Whatever is still standing here
        // belongs to a screen the user has already left.
        this.closeAllModals();

        if (announceOnSettle) {
            this.showNotice(policy);
        }
    }

    /**
   * Closes every open modal so the user is not left looking at a half-open
   * dialog after being redirected to the welcome screen.
   *
   * Resolved via `SESSION_MODAL_CLOSER` — the same token-backed pattern used
   * by `SESSION_LOGOUT_TRANSPORT` — so `libs/core` never imports UI modal
   * libraries directly.
   */
    private closeAllModals(): void {
        try {
            const closer = this.injector.get(SESSION_MODAL_CLOSER, null);
            closer?.closeAll();
        } catch { /* token not bound in this context (e.g. tests without the app providers) */ }
    }

    /**
   * Opens or releases the guard that stops a closed dialog being re-attached
   * while the teardown plays out. Same token, same best-effort handling.
   */
    private setModalTeardownGuard(isActive: boolean): void {
        try {
            const closer = this.injector.get(SESSION_MODAL_CLOSER, null);
            closer?.setTeardownGuard(isActive);
        } catch { /* token not bound in this context (e.g. tests without the app providers) */ }
    }

    private showNotice(policy: SessionEndPolicy): void {
        if (!policy.notice) {
            return;
        }
        // Informational, not an error: being signed out is something to understand,
        // not a failure to act on.
        this.toastr.info(policy.notice.message, policy.notice.title, {
            timeOut: SESSION_NOTICE_TIMEOUT_MS
        });
    }

    /**
   * Fire-and-forget: the user is logged out locally whether or not the server
   * acknowledges it, so a failure here must not block or be surfaced.
   *
   * Deliberately not retried. `clearStoredSession` runs immediately after this
   * dispatches, so any retry would go out without auth headers and be rejected.
   */
    private revokeDeviceSession(): void {
        const macAddress = getLocalStorageValue(StorageKey.DeviceToken);
        // No credentials means there is no server-side session to revoke — the
        // request would be rejected and tells the server nothing it does not know.
        if (!macAddress || !getLocalStorageValue(StorageKey.AuthToken)) {
            return;
        }

        // Resolved lazily: injecting the transport eagerly would rebuild the
        // interceptor chain -> SessionService -> HttpClient -> interceptor chain cycle.
        const transport = this.injector.get<SessionLogoutTransport>(SESSION_LOGOUT_TRANSPORT);

        transport.revokeDeviceSession({ macAddress })
            .pipe(catchError(() => EMPTY))
            .subscribe();
    }

    /** Detaches the stored device lists, then wipes every session-scoped key. */
    private clearStoredSession(scope: SessionAccountScope): void {
        this.detachStoredDevices(scope);
        SESSION_SCOPED_STORAGE_KEYS.forEach(key => removeLocalStorageValue(key));
    }

    /**
   * Empties the cached device list on the accounts this teardown covers, so a
   * stale "logged in on this device" entry is not shown after signing back in.
   * The `userData` list itself survives — it is how the user picks an account
   * to log back into.
   */
    private detachStoredDevices(scope: SessionAccountScope): void {
        const accounts = this.readStoredAccounts();
        const currentUserId = getLocalStorageValue(StorageKey.UserId);
        if (!accounts.length || !currentUserId) {
            return;
        }

        const currentAccount = accounts.find(account => account._id === currentUserId);
        if (!currentAccount) {
            return;
        }

        const shouldDetach = (account: StoredSessionAccount): boolean =>
            scope === SessionAccountScope.PhoneSiblings
                ? account.phone === currentAccount.phone
                : account._id === currentUserId;

        accounts
            .filter(shouldDetach)
            .forEach(account => { account.devices = []; });

        setLocalStorageValue(StorageKey.UserData, JSON.stringify(accounts));
    }

    private readStoredAccounts(): StoredSessionAccount[] {
        const raw = getLocalStorageValue(StorageKey.UserData);
        if (!raw) {
            return [];
        }
        try {
            const parsed: unknown = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed as StoredSessionAccount[] : [];
        } catch {
            return [];
        }
    }
}
